// src/components/AboutSection.styles.ts
//
// Home "professional snapshot" section.
//
// Layout is deliberately editorial rather than dashboard-like: a written
// statement on the left, a spec sheet on the right. The spec sheet is a
// label/value list, not a grid of big numerals — this section exists to
// qualify the reader and hand them to /about, not to display metrics.
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { minDevice } from '../styles/breakpoints';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

export const SnapshotRoot = styled.section`
  position: relative;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.background};
  padding: 4.5rem 0;

  @media ${minDevice.laptop} {
    padding: 6rem 0;
  }
`;

export const SnapshotContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2.5rem;

  @media ${minDevice.laptop} {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    gap: 3.5rem;
  }
`;

/* ---------------------------------------------------------------- left */

export const StatementCol = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

export const Eyebrow = styled(motion.p)`
  margin: 0 0 1rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const Heading = styled(motion.h2)`
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 2rem;
  }
`;

export const Statement = styled(motion.p)`
  margin: 1.25rem 0 0;
  max-width: 46ch;
  font-size: 1.05rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.text};
`;

/* Supporting was removed with the snapshot's second paragraph. It restated the
   capabilities section — APIs, front-ends, relational and document stores, and
   messaging between systems that were not designed to talk — so the section now
   leads straight from its statement to the spec sheet. */

/* Borderless text CTA. The hero already carries a filled primary button and
   an outlined one; a third heavy button one viewport lower would compete with
   the copy rather than support it. */
export const SnapshotCta = styled(motion.button)`
  margin-top: 1.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0 0.35rem;
  background: none;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.headingColor};
  border-radius: 0;
  font-family: ${sansStack};
  font-size: 0.975rem;
  font-weight: 600;
  color: ${({ theme }) => theme.headingColor};
  cursor: pointer;
  transition: gap 0.25s ease, color 0.25s ease, border-color 0.25s ease;

  &::after {
    content: '→';
    font-family: ${monoStack};
    transition: transform 0.25s ease;
  }

  &:hover {
    gap: 0.75rem;
  }

  &:hover::after {
    transform: translateX(2px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

/* --------------------------------------------------------------- right */

export const SpecSheet = styled(motion.aside)`
  align-self: start;
  padding: 1.75rem;
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

export const SpecLabel = styled.p`
  margin: 0 0 0.25rem;
  font-family: ${monoStack};
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const SpecList = styled.dl`
  margin: 0;
  padding: 0;

  /* Hairline between rows, drawn by the row itself so the first row does not
     need a top border and the last does not need an override. */
  & > div + div {
    border-top: 1px solid ${({ theme }) => theme.borderColor};
  }
`;

export const SpecRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.85rem 0;

  &:first-child {
    padding-top: 0.5rem;
  }

  @media ${minDevice.mobile} {
    flex-direction: row;
    justify-content: space-between;
    gap: 1.5rem;
  }
`;

export const SpecTerm = styled.dt`
  margin: 0;
  flex: 0 0 auto;
  font-family: ${monoStack};
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const SpecValue = styled.dd`
  margin: 0;
  min-width: 0;
  font-size: 0.975rem;
  font-weight: 600;
  color: ${({ theme }) => theme.headingColor};
`;

/* The count is given real weight, but not turned into a hero statistic —
   it is one line in a spec sheet, with the four names as its evidence. */
export const DomainsBlock = styled.div`
  margin-top: 0.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const DomainsHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
`;

export const DomainsCount = styled.span`
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.headingColor};
`;

export const DomainsLabel = styled.span`
  font-family: ${monoStack};
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const DomainsList = styled.ul`
  margin: 0.75rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const DomainsItem = styled.li`
  position: relative;
  padding-left: 0.9rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.secondary};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.62em;
    width: 4px;
    height: 4px;
    background: ${({ theme }) => theme.accent};
  }
`;
